import castErrorToBaseError from '@/functions/castErrorToBaseError';
import useTriggerImportMutation from '@/modules/import-export-settings/mutations/useTriggerImportMutation';
import useImportStatusQuery from '@/modules/import-export-settings/queries/useImportStatusQuery';
import { BarcodeOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { IconCart, IconUser, IconWheel } from '@thisisdmg/tirecloud-patterns';
import {
  SolidButton as Button,
  Typography,
  useNotification,
} from '@thisisdmg/tirecloud-patterns/v2';
import { App, Skeleton } from 'antd';
import {
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import classes from './index.module.css';
import config from '@/modules/config/functions/config';

function ImportTriggerItem({
  item,
  disabled,
  setForceDisableButton,
}: {
  item: { key: string; itemKey: string; label: string; icon: ReactNode };
  disabled: boolean;
  setForceDisableButton: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation();
  const notification = useNotification();
  const queryClient = useQueryClient();
  const triggerImportMutation = useTriggerImportMutation();

  return (
    <div
      key={item.key}
      className="tc:flex tc:flex-col tc:md:flex-row tc:md:items-center tc:gap-2 tc:justify-between"
    >
      <div className="tc:flex tc:flex-col tc:md:flex-row tc:items-center tc:grow tc:justify-between">
        <div className="tc:w-full tc:md:w-1/2">
          {t(`import_export_settings.manual_import_category`, {
            category: item.label,
          })}
        </div>
        {disabled || triggerImportMutation.isPending ? (
          <>
            <div className="tc:text-(--t-red) tc:w-full tc:md:w-1/2">
              {t('import_export_settings.wait_while_importing')}
            </div>
            <div />
          </>
        ) : null}
      </div>
      <div>
        <Button
          color="brand"
          disabled={disabled || triggerImportMutation.isPending}
          type="button"
          onClick={() => {
            triggerImportMutation
              .mutateAsync(item.itemKey)
              .then(() => {
                notification.success({
                  message: t('import_export_settings.feedbacks.success', {
                    category: item.label,
                  }),
                });
              })
              .catch((error) => {
                const e = castErrorToBaseError(error);
                notification.error({
                  key: e.id,
                  message: `0x${e.id.toString(16).toUpperCase()}`,
                  description: t(`errors.gen.${e.path}`, { errorId: e.id }),
                });
              })
              .finally(() => {
                setForceDisableButton(false);
                queryClient.invalidateQueries({
                  queryKey: ['import', 'status'],
                });
              });
          }}
        >
          {t(`import_export_settings.import_category_button`, {
            category: item.label,
          })}
        </Button>
      </div>
    </div>
  );
}

function ImportExportSettingsPage() {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [forceDisableButtons, setForceDisableButtons] = useState(false);

  const { data, isFetching, isError, error } = useImportStatusQuery({
    staleTime: 0,
    refetchInterval: 30 * 1000, // refetch every 30 seconds
  });

  useEffect(() => {
    if (isError) {
      const e = castErrorToBaseError(error);
      notification.error({
        key: e.id,
        message: `0x${e.id.toString(16).toUpperCase()}`,
        description: t(`errors.gen.${e.path}`, { errorId: e.id }),
      });
    }
  }, [isError, notification, t, error]);

  return (
    <div>
      <div className={classes.import_export_settings_header}>
        <div>
          <Typography
            variant="hero"
            className={classes.import_export_settings_header__title}
          >
            {t('import_export_settings.title')}
          </Typography>
          <Typography
            variant="body"
            className={classes.import_export_settings_header__subtitle}
          >
            {t('import_export_settings.subtitle')}
          </Typography>
        </div>
      </div>

      {isFetching && !data ? (
        <Skeleton active />
      ) : (
        <div className="tc:flex tc:flex-col tc:gap-y-4">
          {[
            {
              key: 'salesData',
              itemKey: 'sales-data',
              label: t('common.sales_data_other'),
              icon: <BarcodeOutlined />,
            },
            {
              key: 'orders',
              itemKey: 'order',
              label: t('common.order_other'),
              icon: <IconCart />,
            },
            {
              key: 'products',
              itemKey: 'product',
              label: t('common.product_other'),
              icon: <IconWheel />,
            },
            {
              key: 'users',
              itemKey: 'user',
              label: t('common.user_other'),
              icon: <IconUser />,
            },
          ].map((item) => {
            return (
              <ImportTriggerItem
                key={item.key}
                item={item}
                disabled={
                  forceDisableButtons || isError || (data?.isRunning ?? false)
                }
                setForceDisableButton={setForceDisableButtons}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute(
  '/_main-ui/_protected/import-export-settings/'
)({
  component: ImportExportSettingsPage,
  loader: async () => {
    if (config('features.importExportSettings.enabled') !== true)
      throw notFound();
  },
});
